import { useMutation } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface UpdateFieldOptions {
  installationId: number;
  field: string;
  value: any;
}

export function useInstallationFieldUpdate() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ installationId, field, value }: UpdateFieldOptions) => {
      // Special handling for date fields - ensure they're properly formatted
      let processedValue = value;
      if (value instanceof Date) {
        processedValue = value.toISOString();
      } else if (value === '') {
        processedValue = null;
      }

      // Send the update with camelCase field name - backend will transform it
      const response = await apiRequest({
        method: 'PATCH',
        url: `/api/installations/${installationId}`,
        data: {
          [field]: processedValue,
        },
      });

      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate the installation query to refresh data
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${variables.installationId}`],
      });

      // Also invalidate the grid view if it's loaded
      queryClient.invalidateQueries({
        queryKey: ['/api/installations/grid-view'],
      });

      // Show subtle success feedback
      toast({
        description: '✓ Saved',
        duration: 1000,
        className: 'h-8 text-xs',
      });
    },
    onError: (error: any, variables) => {
      console.error('Field update failed:', error);

      toast({
        title: 'Update failed',
        description: error.response?.data?.message || 'Failed to update field',
        variant: 'destructive',
      });

      // Revert optimistic update on error
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${variables.installationId}`],
      });
    },
    // Optimistic update
    onMutate: async ({ installationId, field, value }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [`/api/installations/${installationId}`],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`/api/installations/${installationId}`]);

      // Optimistically update the cache
      queryClient.setQueryData([`/api/installations/${installationId}`], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          [field]: value,
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // If the mutation fails, use the context to roll back
    onSettled: (data, error, variables, context) => {
      if (error && context?.previousData) {
        queryClient.setQueryData(
          [`/api/installations/${variables.installationId}`],
          context.previousData
        );
      }
    },
  });
}

// Batch update hook for updating multiple fields at once
export function useInstallationBatchUpdate() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      installationId,
      updates
    }: {
      installationId: number;
      updates: Record<string, any>
    }) => {
      // Process values (dates, empty strings) but keep camelCase field names
      // Backend will handle field name transformation to match database schema
      const processedUpdates: Record<string, any> = {};

      for (const [key, value] of Object.entries(updates)) {
        // Process value
        let processedValue = value;
        if (value instanceof Date) {
          processedValue = value.toISOString();
        } else if (value === '') {
          processedValue = null;
        }

        processedUpdates[key] = processedValue;
      }

      const response = await apiRequest({
        method: 'PATCH',
        url: `/api/installations/${installationId}`,
        data: processedUpdates,
      });

      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({
        queryKey: [`/api/installations/${variables.installationId}`],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/installations/grid-view'],
      });

      toast({
        description: '✓ Changes saved',
        duration: 1500,
      });
    },
    onError: (error: any) => {
      console.error('Batch update failed:', error);

      toast({
        title: 'Update failed',
        description: error.response?.data?.message || 'Failed to save changes',
        variant: 'destructive',
      });
    },
  });
}